import { motion } from "framer-motion";
import { SERVICE_GROUPS } from "../../lib/content";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

/* Bg gradient per card (Reload-style dark gradient tiles on a lifted panel) */
const TILE_GRADIENTS = [
  "linear-gradient(160deg, #2b0d0d 0%, #0a0303 100%)",
  "linear-gradient(160deg, #1a0a2f 0%, #060a1a 100%)",
  "linear-gradient(160deg, #3a1108 0%, #100404 100%)",
  "linear-gradient(160deg, #1a1a1a 0%, #050505 100%)",
  "linear-gradient(160deg, #2a0a1c 0%, #080308 100%)",
  "linear-gradient(160deg, #0a1a2a 0%, #030812 100%)",
  "linear-gradient(160deg, #221111 0%, #060202 100%)",
];

const TILE_IMGS = [
  "https://images.unsplash.com/photo-1545164415-0fae860defbd?ixlib=rb-4.1.0&auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.1.0&auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.1.0&auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.1.0&auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?ixlib=rb-4.1.0&auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.1.0&auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1517430816045-df4b7de11d1d?ixlib=rb-4.1.0&auto=format&fit=crop&w=900&q=80",
];

export default function ServicesGrid() {
  return (
    <section
      className="relative py-28 md:py-40 bg-cream text-neutral-900"
      data-testid="services-section"
      id="services-teaser"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Editorial headline like reference: "Everything your brand needs to grow" */}
        <div className="text-center max-w-5xl mx-auto">
          <div className="flex justify-center mb-6">
            <div className="tag-glass !text-neutral-900 !bg-black/5 !border-neutral-900/15" style={{ color: "#111", background: "rgba(0,0,0,0.04)", borderColor: "rgba(0,0,0,0.12)" }}>
              <span className="dot" /> The ecosystem · seven disciplines
            </div>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="font-display leading-[1.02] tracking-tight text-4xl md:text-6xl text-neutral-900"
          >
            <span>Everything</span>{" "}
            <span className="italic-accent text-neutral-500">your brand</span>
            <br />
            <span className="italic-accent text-neutral-500">needs</span>{" "}
            <span className="inline-pill" aria-hidden="true">
              <Sparkles />
            </span>{" "}
            <span>to grow.</span>
          </motion.h2>
          <p className="mt-8 text-neutral-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Owned media is the base layer. Every other discipline plugs into it
            — so a campaign behaves like infrastructure, not a line item.
          </p>
        </div>

        {/* Layered card row — center card lifted, side cards receding (Reload-style) */}
        <div className="relative mt-16 md:mt-24">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4 md:gap-3 items-end">
            {SERVICE_GROUPS.map((s, i) => {
              const isCenter = i === 2 || i === 3; // lift the middle cards
              const spanClass = "col-span-1";
              return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.75, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                  className={`${spanClass} ${isCenter ? "md:-translate-y-6 md:z-10" : ""} arc-card rounded-2xl overflow-hidden text-white`}
                  style={{ background: TILE_GRADIENTS[i] }}
                  data-testid={`service-card-${s.key}`}
                >
                  <Link to="/services" className="block h-full">
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img
                        src={TILE_IMGS[i]}
                        alt={s.title}
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity"
                      />
                      <div
                        className="absolute inset-0"
                        style={{ background: TILE_GRADIENTS[i], mixBlendMode: "multiply" }}
                      />
                      <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.25em] text-white/85 bg-black/40 backdrop-blur px-2 py-1 rounded-full border border-white/15">
                        {s.tag.split(" · ")[0]}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <div className="font-display text-white text-lg md:text-xl leading-tight tracking-tight">
                          {s.title}
                        </div>
                        <div className="mt-2 text-white/72 text-[11px] leading-relaxed line-clamp-3">
                          {s.desc}
                        </div>
                        <div className="mt-4 flex items-center justify-between text-white/72">
                          <span className="font-mono uppercase tracking-[0.22em] text-[9px]">
                            {s.tag.split(" · ")[1]}
                          </span>
                          <ArrowUpRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-14 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <p className="text-neutral-500 text-sm max-w-md">
              From brand core to final detail — we build presence that
              compounds across every screen we own.
            </p>
            <Link
              to="/services"
              className="btn btn-glass-dark"
              data-testid="services-explore-all"
            >
              Explore all services
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
